import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { FormSection } from '@/shared/ui/components/form/FormSectionCollapsible';
import { ChipSelectMulti, ChipSelectSingle } from '@/shared/ui/components/form/ChipSelect';
import { CLIENT_ROLE_VALUES, clientRoleLabelKey } from '../../utils/client-roles';
import { getClientTypeOptions, type ClientFormData } from './schema';
import { labelStyle, errorStyle } from './drawer-styles';
import { Label, Text, TextInput } from '@/shared/ui';

interface PersonalDataSectionProps {
  form: UseFormReturn<ClientFormData>;
}

/**
 * Minimal personal data: person type toggle, full name and the client's
 * roles (several at once, `demand_profile.client_roles`).
 * Documents (CPF/CNPJ) live in IdentityExtraSection (collapsed).
 */
export const PersonalDataSection: React.FC<PersonalDataSectionProps> = ({ form }) => {
  const { t } = useTranslation('features/clients');
  const errors = form.formState.errors;

  return (
    <FormSection title={t('drawer.personalData.title')} variant="quiet">
      <Controller
        name="identity.personType"
        control={form.control}
        render={({ field }) => (
          <ChipSelectSingle
            label=""
            options={getClientTypeOptions(t)}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <Controller
        name="personalInfo.fullName"
        control={form.control}
        render={({ field }) => (
          <div className="flex flex-col">
            <Label style={labelStyle}>
              {t('drawer.personalData.fullName')}
              <Text component="span" className="ml-(--space-1) text-(--status-error)">*</Text>
            </Label>
            <TextInput
              {...field}
              placeholder={t('drawer.personalData.fullNamePlaceholder')}
              maxLength={120}
            />
            {errors.personalInfo?.fullName?.message && (
              <Text component="span" style={errorStyle}>
                {errors.personalInfo.fullName.message}
              </Text>
            )}
          </div>
        )}
      />

      <Controller
        name="clientRoles"
        control={form.control}
        render={({ field }) => (
          <ChipSelectMulti
            label={t('drawer.personalData.roles')}
            options={CLIENT_ROLE_VALUES.map((role) => ({ value: role, label: t(clientRoleLabelKey(role)) }))}
            value={field.value ?? []}
            onChange={field.onChange}
            optionsTestId="client-roles-chips"
          />
        )}
      />
    </FormSection>
  );
};
